import React, { useEffect, useState } from "react";
import ListingTable from "./ListingTable";
import userServices from "services/userServices";
import dayjs from "dayjs";
import { useNotification } from "providers/NotificationProvider";
import { useAuth } from "context/AuthContext";
import DeleteModal from "../../../modals/DeleteModal";
import { useTranslation } from "context/TranslationProvider";
import UserProfileDetailsModal from "../../../modals/UserProfileDetailsModal";

const AllListings = () => {
  const [startDate, setStartDate] = useState();
  const { t } = useTranslation();
  const [searchFrom, setSearchFrom] = useState("");
  const [searchTo, setSearchTo] = useState("");
  const today = dayjs();
  const [statusLoadingId, setStatusLoadingId] = useState(null);

  const deleteRow = (item) => {
    setItemToDelete(item);
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setItemToDelete(null);
  };

  const deleteListing = () => {
    if (!itemToDelete?._id) return;

    setStatusLoadingId(itemToDelete._id);
    userServices
      .deleteUsersListing({ deletedListingId: itemToDelete._id }, itemToDelete.type)
      .then((res) => {
        const payload = res?.data || res;
        if (payload?.success) {
          notify("success", t(payload?.messageKey || "page.userListings.toast.listingDeleted", payload?.message || t("page.userListings.toast.listingDeleted", "Row deleted successfully")));
          setUserListings((prevItems) =>
            prevItems.map((item) =>
              item._id === itemToDelete._id
                ? { ...item, isDeleted: true, status: "closed" }
                : item
            )
          );
        } else {
          notify("error", t(payload?.messageKey || payload?.error || "toast.somethingWentWrong", payload?.message || t("toast.somethingWentWrong", "Something went wrong while creating the listing")));
        }
      })
      .catch((err) => {
        console.error(err);
        const errPayload = err?.response?.data || {};
        notify("error", t(errPayload.messageKey || errPayload.error || "toast.somethingWentWrong", errPayload.message || t("toast.somethingWentWrong", "Something went wrong while creating the listing")));
      })
      .finally(() => {
        setStatusLoadingId(null);
        handleModalClose();
      });
  };

  const [isItemsLoading, setIsItemsLoading] = useState(true);
  const [userListings, setUserListings] = useState([]);
  const [meta, setMeta] = useState({ totalItems: 0, totalPages: 1, page: 1, limit: 20 });
  const [tableState, setTableState] = useState({
    page: 1,
    limit: 20,
    sortBy: "date",
    sortOrder: -1,
    status: "",
    listingType: "",
    search: {},
    refreshTrigger: 0
  });

  const [openUserModal, setOpenUserModal] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);

  const handleOpenUserDetails = async (userId) => {
    if (!userId) return;
    try {
      const res = await userServices.getUserDetails(userId);
      const fullUser = res?.data || res;
      if (fullUser) {
        setSelectedUserDetails(fullUser);
        setOpenUserModal(true);
      } else {
        notify("error", t("page.orders.noOrderDetails", "No user details found."));
      }
    } catch (err) {
      notify("error", "Failed to load user details");
    }
  };

  const { isAuthenticated } = useAuth();
  const [itemToDelete, setItemToDelete] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const { notify } = useNotification();


  const [selectedFrom, setSelectedFrom] = useState({});
  const [selectedTo, setSelectedTo] = useState({});
  const [filteredCities, setFilteredCities] = useState([]);
  const [isSelectedFrom, setIsSelectedFrom] = useState(false);
  const [isSelectedTo, setIsSelectedTo] = useState(false);
  const [rotate, setRotate] = useState(false);
  const [noCityMatchMessage, setNoCityMatchMessage] = useState("");

  const filterItems = async (term, type) => {
    if (!term || term.length < 1) {
      setFilteredCities([]);
      return;
    }

    try {
      const res = await userServices.fetchPlaceSuggestions(term, type);
      const suggestions = res.data || [];
      
      setFilteredCities(suggestions);

      if (suggestions.length === 0) {
        const template =
          type === "from"
            ? t("page.allListings.noFromMatch", "No listings found from \"{city}\"")
            : t("page.allListings.noToMatch", "No listings found to \"{city}\"");

        const message = (template || "No results found for {city}").replace("{city}", term);
        setNoCityMatchMessage(message);
      } else {
        setNoCityMatchMessage("");
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setFilteredCities([]);
    }
  };

  const handleFromChange = (e) => {
    const searchTerm = e.target.value;
    setSearchFrom(searchTerm);

    if (searchTerm !== "") {
      setIsSelectedFrom(false);
      setSelectedFrom({});
      filterItems(searchTerm, "from");
    } else {
      setSelectedFrom({});
      setIsSelectedFrom(false);
      setFilteredCities([]);
      setNoCityMatchMessage("");
    }
  };

  const handleToChange = (e) => {
    const searchTerm = e.target.value;
    setSearchTo(searchTerm);

    if (searchTerm !== "") {
      setIsSelectedTo(false);
      setSelectedTo({});
      filterItems(searchTerm, "to");
    } else {
      setSelectedTo({});
      setIsSelectedTo(false);
      setFilteredCities([]);
      setNoCityMatchMessage("");
    }
  };

  const handleFromCity = (selectedCity) => {
    setSelectedFrom(selectedCity);
    setSearchFrom(selectedCity.name);
    setIsSelectedFrom(true);
    setFilteredCities([]);
    setNoCityMatchMessage("");
  };

  const handleToCity = (selectedCity) => {
    setSelectedTo(selectedCity);
    setSearchTo(selectedCity.name);
    setIsSelectedTo(true);
    setFilteredCities([]);
    setNoCityMatchMessage("");
  };

  const handleTableChange = (type, value) => {
    if (type === "sort") {
      const { sortField, sortOrder } = value;
      const newOrder = sortOrder === "asc" ? 1 : -1;
      setTableState((prev) => ({
        ...prev,
        sortBy: sortField,
        sortOrder: newOrder,
        page: 1,
      }));
    }

    if (type === "pagination") {
      const { page, sizePerPage } = value;
      setTableState((prev) => ({
        ...prev,
        page: page !== undefined ? page : prev.page,
        limit: sizePerPage !== undefined ? sizePerPage : prev.limit,
      }));
    }

    if (type === "filter") {
      const { filters } = value;
      setTableState((prev) => ({
        ...prev,
        ...filters,
        page: 1,
      }));
    }
  };

  const handleSearch = () => {
    const searchData = {};

    if (isSelectedFrom && selectedFrom?.name) {
      searchData.fromSearch = selectedFrom.name;
    } else if (searchFrom) {
      searchData.fromSearch = searchFrom;
    }
    
    if (isSelectedTo && selectedTo?.name) {
      searchData.toSearch = selectedTo.name;
    } else if (searchTo) {
      searchData.toSearch = searchTo;
    }

    if (startDate) searchData.date = dayjs(startDate).toISOString();

    setTableState(prev => ({
      ...prev,
      page: 1,
      search: searchData,
      refreshTrigger: (prev.refreshTrigger || 0) + 1
    }));
  };

  const getAllListings = () => {
    const { page, limit, sortBy, sortOrder, status, listingType, search } = tableState;

    setIsItemsLoading(true);
    userServices
      .getAllListings({
        page,
        limit,
        sortBy,
        sortOrder,
        status,
        listingType,
        search: JSON.stringify(search)
      })
      .then((res) => {
        const payload = res?.data?.data || res?.data?.listings || res?.data || res;
        const listings = Array.isArray(payload) ? payload : (payload.listings || []);
        const pagination = res?.data?.pagination || res?.data?.meta || res?.pagination || res?.meta || { totalItems: 0, totalPages: 1, page: 1, limit: 20 };

        setUserListings(listings);
        setMeta(pagination);
      })
      .catch((err) => {
        console.error(err);
        notify("error", t("toast.somethingWentWrong", "Something went wrong while creating the listing"));
      })
      .finally(() => {
        setIsItemsLoading(false);
      });
  };

  useEffect(() => {
    getAllListings();
    // eslint-disable-next-line
  }, [
    tableState.page,
    tableState.limit,
    tableState.sortBy,
    tableState.sortOrder,
    tableState.status,
    tableState.listingType,
    tableState.search,
    tableState.refreshTrigger
  ]);


  const toggleFromTo = () => {
    setRotate((prev) => !prev);
    const newFrom = selectedTo || {};
    const newTo = selectedFrom || {};
    setSelectedFrom(newFrom);
    setSelectedTo(newTo);
    setSearchFrom(newFrom?.name || "");
    setSearchTo(newTo?.name || "");
  };


  const handleToggleAccount = async (row, status, type, reason = null, remark = null) => {
    setIsStatusUpdating(true);
    try {
      setStatusLoadingId(row._id);
      const res = await userServices.updateListings(row._id, {
        status,
        type,
        userData: row.userData,
        adminRemarks: remark,
        selectedReason: reason,
      });

      const payload = res?.data || res;
      if (payload?.success) {
        notify("success", t(payload?.messageKey || "page.userListings.toast.listingUpdated", payload?.message || t("page.userListings.toast.listingUpdated", "Listing updated successfully")));
        getAllListings();
      } else {
        const errorMessageKey = payload?.messageKey || payload?.error || "page.userListings.toast.listingStatusUpdateFailed";
        notify("error", t(errorMessageKey, payload?.message || t("page.userListings.toast.listingStatusUpdateFailed", "Failed to update listing status")));
      }
    } catch (err) {
      const errPayload = err.response?.data || {};
      const errorMessageKey = errPayload.messageKey || errPayload.error || "page.userListings.toast.listingStatusUpdateFailed";
      notify("error", t(errorMessageKey, errPayload.message || t("page.userListings.toast.listingStatusUpdateFailed", "Failed to update listing status")));
    } finally {
      setIsStatusUpdating(false);
      setStatusLoadingId(null);
    }
  };

  const handleClearFilters = () => {
    setSearchFrom("");
    setSearchTo("");
    setStartDate(undefined);
    setSelectedFrom({});
    setSelectedTo({});
    setIsSelectedFrom(false);
    setIsSelectedTo(false);
    setFilteredCities([]);
    setNoCityMatchMessage("");
    setTableState((prev) => ({
      ...prev,
      page: 1,
      status: "",
      listingType: "",
      search: {},
      refreshTrigger: (prev.refreshTrigger || 0) + 1
    }));
  };

  return (
    <main className="flex items-center justify-center card-header-full-height">
      <section
        className={`w-full ${isAuthenticated ? "md:w-full" : "md:w-10/12"}`}
      >
        <ListingTable
          items={userListings}
          isItemsLoading={isItemsLoading}
          handleToggleAccount={handleToggleAccount}
          rotate={rotate}
          searchFrom={searchFrom}
          searchTo={searchTo}
          isSelectedFrom={isSelectedFrom}
          isSelectedTo={isSelectedTo}
          filteredCities={filteredCities}
          handleFromChange={handleFromChange}
          handleToChange={handleToChange}
          handleFromCity={handleFromCity}
          handleToCity={handleToCity}
          toggleFromTo={toggleFromTo}
          startDate={startDate}
          setStartDate={setStartDate}
          today={today}
          onSearchClick={handleSearch}
          noCityMatchMessage={noCityMatchMessage}
          deleteRow={deleteRow}
          onTableChange={handleTableChange}
          meta={meta}
          currentStatus={tableState.status}
          currentListingType={tableState.listingType}
          sortBy={tableState.sortBy}
          sortOrder={tableState.sortOrder}
          isStatusUpdating={isStatusUpdating}
          statusLoadingId={statusLoadingId}
          onClearFilters={handleClearFilters}
          handleOpenUserDetails={handleOpenUserDetails}
        />
        <DeleteModal
          isOpen={openModal}
          toggle={handleModalClose}
          itemName="listing"
          onDelete={deleteListing}
          loading={statusLoadingId !== null}
        />
        <UserProfileDetailsModal
          isOpen={openUserModal}
          toggle={() => setOpenUserModal(false)}
          user={selectedUserDetails}
        />
      </section>
    </main>
  );
};

export default AllListings;